import { useEffect } from 'react'
import { createPortal } from 'react-dom'
import type { ReactNode } from 'react'
import { cn } from '@/lib/cn'

export type ModalSize = 'sm' | 'md' | 'lg'

export interface ModalProps {
  open: boolean
  onClose: () => void
  title?: ReactNode
  subtitle?: ReactNode
  children: ReactNode
  /** Pasek akcji na dole (przyciski). */
  footer?: ReactNode
  size?: ModalSize
  /** Blokuje zamknięcie klawiszem Esc i kliknięciem w tło. */
  persistent?: boolean
  className?: string
}

const SIZES: Record<ModalSize, string> = {
  sm: 'max-w-[420px]',
  md: 'max-w-[560px]',
  lg: 'max-w-[820px]',
}

/** Modal renderowany w portalu; zamykany Esc i kliknięciem w tło. */
export function Modal({
  open,
  onClose,
  title,
  subtitle,
  children,
  footer,
  size = 'md',
  persistent = false,
  className,
}: ModalProps) {
  useEffect(() => {
    if (!open) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !persistent) onClose()
    }
    document.addEventListener('keydown', onKeyDown)
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.body.style.overflow = previousOverflow
    }
  }, [open, onClose, persistent])

  if (!open) return null

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-[rgba(10,31,46,0.45)] p-6 pt-[10vh]"
      onMouseDown={(event) => {
        if (!persistent && event.target === event.currentTarget) onClose()
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        className={cn('w-full rounded-lg bg-card shadow-modal', SIZES[size], className)}
      >
        {title ? (
          <header className="flex items-start justify-between gap-4 border-b border-line-soft px-5 py-4">
            <div className="min-w-0">
              <h2 className="t-h2">{title}</h2>
              {subtitle ? <p className="t-small mt-1">{subtitle}</p> : null}
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Zamknij"
              className="-mr-1 -mt-1 h-7 w-7 shrink-0 rounded text-muted transition-colors duration-fast hover:bg-[#eef1f4] hover:text-ink"
            >
              ×
            </button>
          </header>
        ) : null}
        <div className="px-5 py-4">{children}</div>
        {footer ? (
          <footer className="flex items-center justify-end gap-2 border-t border-line-soft px-5 py-3.5">{footer}</footer>
        ) : null}
      </div>
    </div>,
    document.body,
  )
}
